<template>
    <div v-if="shortlist" ref="shortlistElement" class="shortlist-header">
        <VJoyPanel
            :class="{
                'shortlist-header__fixed': scrollY > MARGIN_BEFORE_SHORTLIST,
                'shortlist-header__hidden': !isShortlistPaginationNotOverlap,
            }"
        >
            <template #panel-body>
                <VJoyWrapper justify="space-between" row-gap="2">
                    <VJoyWrapper align="flex-start" direction="column" row-gap="0" no-margin class="shortlist-header__title">
                        {{ t('profile.shortlist.title.my.selection') }}
                    </VJoyWrapper>

                    <VJoyWrapper justify="center" direction="column" no-margin class="shortlist-header__items">
                        <VJoyAvatarsList>
                            <VJoyWrapper justify="center" wrap="nowrap" no-margin column-gap="2">
                                <div
                                    v-for="(item, index) in shortlist.profiles"
                                    :key="item.profileId"
                                    class="shortlist-header__items-profile-selected"
                                >
                                    <VJoyAvatar
                                        :photo-url="item.photo.absoluteUrl"
                                        :data-testid="`profile-shortlist-redirect-profile-${index}`"
                                        :link="true"
                                        @click="redirectToProfile(item.url)"
                                    />
                                    <VJoyIcon
                                        :data-testid="`profile-shortlist-remove-${index}`"
                                        name="cross-circle"
                                        role="button"
                                        :aria-label="t('general.delete')"
                                        @click="shortlistStore.remove(item.profileId)"
                                        @keydown.enter="shortlistStore.remove(item.profileId)"
                                    />
                                </div>
                                <VJoyAvatar v-for="index in remainingEmptyItems" :key="index" is-empty :photo-url="BLANK_PICTURE" />
                            </VJoyWrapper>
                        </VJoyAvatarsList>
                    </VJoyWrapper>

                    <div class="shortlist-header__actions">
                        <VJoyButton
                            class="profile-cta-project"
                            data-testid="shortlist-project-proposal-cta"
                            variant="main"
                            size="small"
                            :loading="proposeProjectUrl == null"
                            :href="proposeProjectUrl"
                        >
                            {{ t('show.contactme.button.label') }}
                        </VJoyButton>

                        <VJoyButton
                            data-testid="shortlist-delete"
                            :label="t('profile.shortlist.reset')"
                            variant="ghost"
                            size="small"
                            @click="shortlistStore.deleteShortlist"
                        />
                    </div>
                </VJoyWrapper>
            </template>
        </VJoyPanel>
    </div>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref, useTemplateRef} from 'vue';
    import {useRoute} from '@typed-router';
    import {useElementBounding, useWindowScroll} from '@vueuse/core';
    import {storeToRefs} from 'pinia';
    import {onBeforeUnmount, useTranslation} from '#imports';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyAvatar, VJoyAvatarsList, VJoyButton, VJoyIcon, VJoyPanel, VJoyWrapper} from '@maltjoy/core-vue';
    import {useProfilesStore} from '../stores/profiles.store';
    import {useSearchUrlStore} from '../stores/search-url.store';
    import {useShortlistStore} from '../stores/shortlist.store';

    const BLANK_PICTURE = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" style="background-color: white"/>';
    const MARGIN_BEFORE_SHORTLIST = 24;
    const {t} = useTranslation();
    const route = useRoute();

    const shortlistElement = useTemplateRef('shortlistElement');
    const paginationElement = ref<HTMLElement | null>(import.meta.client ? (document.querySelector('.search-results-footer') as HTMLElement) : null);

    const {right: shortlistRight, left: shortlistLeft, bottom: shortlistBottom, top: shortlistTop} = useElementBounding(shortlistElement);
    const {right: paginationRight, left: paginationLeft, bottom: paginationBottom, top: paginationTop} = useElementBounding(paginationElement.value);
    const {y: scrollY} = useWindowScroll();

    const isShortlistPaginationNotOverlap = computed(
        (): boolean =>
            shortlistRight.value < paginationLeft.value ||
            shortlistLeft.value > paginationRight.value ||
            shortlistBottom.value < paginationTop.value ||
            shortlistTop.value > paginationBottom.value,
    );

    const shortlistStore = useShortlistStore();
    const {shortlist} = storeToRefs(shortlistStore);
    const profilesStore = useProfilesStore();
    const {searchId} = storeToRefs(profilesStore);
    const searchUrlStore = useSearchUrlStore();

    const remainingEmptyItems = computed(() => shortlist.value!.maxItems - shortlist.value!.profiles.length);

    const proposeProjectUrl = computed(() => {
        if (searchId.value && shortlist.value?.title) {
            const queryParams = new URLSearchParams();
            queryParams.set('searchId', searchId.value);
            queryParams.set('entryPoint', 'search_page');

            if (route.query.sourceComponent) {
                queryParams.set('sourceComponent', route.query.sourceComponent as string);
            }

            return `/client/briefs/proposal?${queryParams.toString()}`;
        }
        return null;
    });

    function reloadShortlist() {
        shortlistStore.fetchShortlist();
    }

    function redirectToProfile(url: string) {
        return safelyNavigateTo(`${url}?${searchUrlStore.toUrlQueryParams({searchid: searchId.value}).toString()}`, {externalToThisApp: true});
    }

    onMounted(() => {
        window.addEventListener('focus', reloadShortlist);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('focus', reloadShortlist);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .shortlist-header {
        position: sticky;
        top: var(--search-page-header-height);
        z-index: 19;
        overflow: hidden;

        &__fixed {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
            box-shadow: var(--joy-core-elevation-2);
        }

        &__hidden {
            display: none;
        }

        &__title {
            font-family: var(--joy-font-family-title);
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-primary-600);
            line-height: var(--joy-line-height-small);
            flex: 1;
            @include joy.screen_md {
                display: none;
            }

            strong {
                display: block;
            }
        }

        &__items {
            flex: 3;
            &-profile-selected {
                position: relative;
                cursor: pointer;

                .joy-icon {
                    position: absolute;
                    opacity: 0;
                    top: 0;
                    right: 0;
                    height: 16px;
                    cursor: pointer;
                    border-radius: 100%;
                    background-color: var(--joy-color-background-default);
                }

                &:hover {
                    .joy-icon {
                        opacity: 1;
                    }
                }
            }
        }

        &__actions {
            flex: 2;
            text-align: center;
        }
    }
</style>
